import { QueryClient } from '@tanstack/react-query';

import { createApiClient, type ApiClient, type ApiError } from '@platform/api-client';
import { ANONYMOUS_SESSION, SESSION_QUERY_KEY } from '@platform/api-client/react';

/**
 * The super admin console's one API client and query cache (M2.8).
 *
 * Both live here rather than in `main.tsx` because they refer to each other: a `401`
 * from any endpoint has to put the session cache back to "anonymous", and the session
 * cache is what every guard, the nav and the banner read. The same shape as the patient
 * app's `lib/api/client.ts`, for the same reason.
 *
 * ## No persister, ever
 *
 * TanStack Query's cache stays in memory only. This console reads across tenants, so
 * anything written to `localStorage` or IndexedDB would put another clinic's data in an
 * operator's browser (ADR-007, SECURITY.md §7).
 *
 * ## Why neither callback navigates
 *
 * The router does not exist when the client is created, and two things that can move the
 * operator is one too many. `onUnauthenticated` records the fact; `RequireAuth` - which
 * knows the route, and therefore what `?next=` should be - does the navigating.
 * `onStepUpRequired` hands the error to whoever is listening, which is the step-up
 * provider inside the tree.
 */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

/* ------------------------------------------------------------------ *
 * The step-up bridge
 * ------------------------------------------------------------------ */

type StepUpListener = (error: ApiError) => void;

let stepUpListener: StepUpListener | null = null;

/**
 * Subscribes to `403 MFA_STEP_UP_REQUIRED` from anywhere.
 *
 * One listener, not a set: there is exactly one step-up dialog in the app, and a second
 * subscriber would mean two dialogs racing for the same verification. The provider
 * installs itself on mount and removes itself on unmount.
 */
export function subscribeToStepUp(listener: StepUpListener): () => void {
  stepUpListener = listener;
  return () => {
    if (stepUpListener === listener) stepUpListener = null;
  };
}

export const apiClient: ApiClient = createApiClient({
  /*
   * Same origin. In development Vite proxies `/api` to the API with
   * `changeOrigin: false`, so the host the browser used survives the hop - which for
   * this console is the *platform* host, the one with no tenant (ARCHITECTURE.md §4.1).
   */
  baseUrl: '',

  onUnauthenticated: () => {
    /*
     * Seed rather than invalidate: refetching would answer `authenticated: false`
     * anyway, and doing it optimistically means the guard redirects on the same tick
     * instead of after a round trip.
     */
    queryClient.setQueryData(SESSION_QUERY_KEY, ANONYMOUS_SESSION);
  },

  onStepUpRequired: (error) => {
    stepUpListener?.(error);
  },
});
